import React from 'react';
import TelnyxLogo from '@svg/telnyx';
import styled from 'styled-components';

import { Button, Modal } from '@common/index';
import { useTheme } from '../../../../styles';
import { Checkbox, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Stack, alpha } from '@mui/material';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 6px;

  p {
    color: ${({ theme }) => alpha(theme.text.standard, 0.6)};
    font-size: 14px;
  }

  gap: 1.5rem;

  & .MuiListItemText-secondary {
    max-width: 97%;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }

  & .MuiListItemIcon-root {
    min-width: 40px;
  }
`;

const ListContainer = styled.div`
  display: flex;
  width: 97%;
`;

export type DevdocsCollection = {
  name: string;
  description: string;
  path: string;
};

type CollectionProps = {
  collection: DevdocsCollection;
  selected: string[];
  setSelected: React.Dispatch<React.SetStateAction<string[]>>;
};

const CollectionListItem: React.FC<CollectionProps> = ({ collection, selected, setSelected }) => {
  const handleCheckboxToggle = () => {
    if (selected.includes(collection.path)) setSelected(selected.filter((path) => path !== collection.path));
    else setSelected(selected.concat(collection.path));
  };

  return (
    <ListItem disablePadding sx={{ marginBottom: '4px' }}>
      <ListItemButton onClick={() => handleCheckboxToggle()} dense sx={{ borderRadius: '8px' }}>
        <ListItemIcon>
          <Checkbox edge='start' checked={selected.includes(collection.path)} tabIndex={-1} disableRipple />
        </ListItemIcon>
        <ListItemText primary={collection.name} secondary={collection.description} />
      </ListItemButton>
    </ListItem>
  );
};

interface Props {
  open: string | null;
  page: number;
  setOpen: React.Dispatch<React.SetStateAction<string | null>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  reset: () => void;
  configuration: {
    selected: string[];
    setSelected: React.Dispatch<React.SetStateAction<string[]>>;
  };
}

export const DevdocsCollections: React.FC<Props> = ({ open, page, setPage, configuration, reset }) => {
  const theme = useTheme();

  const { selected, setSelected } = configuration;

  const collections = [
    {
      name: 'Programmable Voice',
      description: 'This collection includes the call control documents from the voice collection.',
      path: '/docs/voice/programmable-voice',
    },
    {
      name: 'Messaging',
      description: 'This collection includes all forms of message sending (SMS, MMS, alpha-numeric, short-code, etc)',
      path: '/docs/messaging',
    },
    {
      name: 'Numbers',
      description: 'This collection includes all number related operations (buying, releasing, updating, etc).',
      path: '/docs/numbers',
    },
    {
      name: 'Programmable Fax',
      description: 'This collection includes all fax related operations (sending, receiving, etc).',
      path: '/docs/programmable-fax',
    },
    {
      name: 'SIP Trunking Guides',
      description: 'This collection includes all the SIP trunking setup guides from the voice collection.',
      path: '/docs/voice/sip-trunking',
    },
    {
      name: 'WebRTC',
      description: 'This collection includes the WebRTC documents from the voice collection.',
      path: '/docs/voice/webrtc',
    },
    {
      name: 'Account',
      description: 'This collection includes all account related operations (billing, account management, etc).',
      path: '/docs/account',
    },
    {
      name: 'Debugging',
      description: 'This collection includes the detailed record search endpoints.',
      path: '/docs/debugging',
    },
    {
      name: 'Cloud Storage',
      description: 'This collection includes all cloud storage related operations (uploading, downloading, etc).',
      path: '/docs/cloud-storage',
    },
    {
      name: 'Inference',
      description: 'This collection includes all inference related operations (completions, summary, embeddings, etc).',
      path: '/docs/inference',
    },
    {
      name: 'IoT SIM',
      description: 'This collection includes all IoT SIM related operations (activating, deactivating, etc).',
      path: '/docs/iot-sim',
    },
    {
      name: 'Network',
      description: 'This collection includes all network related operations (IP, IP pools, etc).',
      path: '/docs/network',
    },
    {
      name: 'Video',
      description: 'This collection includes all video related operations (create room, add participant, etc).',
      path: '/docs/video',
    },
    {
      name: 'Identity',
      description: 'This collection includes number lookups and 2FA verification codes.',
      path: '/docs/identity',
    },
    {
      name: 'Development',
      description: 'This collection includes a number of getting started guides and general info.',
      path: '/docs/development',
    },
  ];

  const body = (
    <Container>
      <p>
        Select all the products that you would like to include in this loader. Selecting a product will pull all
        documents related to that product and upload them to your bucket.
      </p>
      <ListContainer>
        <List sx={{ width: '100%' }}>
          {collections?.map((collection: DevdocsCollection) => (
            <CollectionListItem
              key={collection.name}
              collection={collection}
              selected={selected}
              setSelected={setSelected}
            />
          ))}
        </List>
      </ListContainer>
    </Container>
  );

  const allSelected = collections?.length === selected.length;

  const actions = (
    <Stack width='100%' direction='row' display='flex' justifyContent='space-between'>
      {allSelected ? (
        <Button onClick={() => setSelected([])}>Deselect All</Button>
      ) : (
        <Button onClick={() => setSelected(collections.map((collection: DevdocsCollection) => collection.path))}>
          Select All
        </Button>
      )}
      <Stack spacing={1} direction='row'>
        <Button onClick={() => setPage(0)}>Back</Button>
        <Button disabled={selected.length === 0} customColor='primary' onClick={() => setPage(2)}>
          Load Categories
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <Modal
      open={open === 'devdocs' && page === 1}
      setOpen={reset}
      page={page + 1}
      total_pages={4}
      background={alpha(theme.text.standard, 0.06)}
      header='Devdocs'
      icon={<TelnyxLogo height={17} color={theme.text.standard} />}
      body={body}
      actions={actions}
    />
  );
};
